import { Component, ElementRef, Input, OnInit, OnDestroy, ChangeDetectorRef, Renderer2, ViewEncapsulation } from '@angular/core';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { WidgetBase } from 'common/panel-layout/widget-base';
import { ThemeServiceHelper } from 'common/theme/service.helper';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { CannedMessageRequest } from 'common/canned-messages/canned-message-request.interface';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { WorkItemEmail } from 'common/workitems/models/work-item-email';
import { WorkItemsTrackingService } from 'common/workitems/workitems-tracking.service';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { BcConfigService } from 'common/config/provider';
import { BcVisitorInfoBuilderService } from '../shared/services/visitor-info-builder.service';
import { LaneManagerService } from 'shared/components/lane-buttons/shared/services/lane-manager/lane-manager.service';
import { WorkItemLaneType } from 'shared/components/lane-buttons/shared/services/lane-manager/work-item-lane-type.enum';
import { CannedMessagePanelComponent } from 'shared/components/canned-message-panel/canned-message-panel.component';
import { EmailsService } from 'common/emails/emails.service';

@Component({
  selector: 'app-email-widget',
  templateUrl: './email-widget.component.html',
  styleUrls: ['./email-widget.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EmailWidgetComponent extends WidgetBase implements OnInit, OnDestroy {
  @Input()
  public workItem: WorkItemEmail;

  public isMultiLane;
  public cannedMessageOpenSubscription;
  public cannedMessageCloseSubscription;
  public cannedMessageSelectionSubscription;
  public emailWorkItemUpdateSubscription;
  public emailThread;
  public lastEmail;
  public themeClass;
  public visitor;
  public isCannedMessagePanelOpen;
  public cannedMessage: CannedMessageRequest;
  public workItemLanes;
  private _trackSelectedItemSubscription: Subscription;
  private _waitForTrack = false;
  private _dataIsLoaded = false;
  private _laneObservable: Subscription;

  constructor(
    private _xlcEmails: EmailsService,
    private _bcAnalytics: BcAnalytics,
    bcConfig: BcConfigService,
    element: ElementRef,
    private _bcVisitorInfoBuilderService: BcVisitorInfoBuilderService,
    bcWorkAreaManager: WorkAreaManager,
    private _rxEmitter: RxEmitter,
    private _bcWorkItemsTrackingService: WorkItemsTrackingService,
    private _changeDetector: ChangeDetectorRef,
    renderer: Renderer2,
    private _laneManager: LaneManagerService
  ) {
    super(element.nativeElement, bcWorkAreaManager, renderer, _rxEmitter);

    this.isMultiLane = bcConfig.current.features.multiLane;

    this.cannedMessageOpenSubscription = null;
    this.cannedMessageCloseSubscription = null;
    this.cannedMessageSelectionSubscription = null;
    this.emailWorkItemUpdateSubscription = null;
  }

  ngOnInit(): void {
    this.initTrackEvents();
    this.initMessageBus();
    this.initLaneObservable();
    this.initEmail();
  }

  ngOnDestroy(): void {
    if (this.cannedMessageOpenSubscription) {
      this.cannedMessageOpenSubscription.unsubscribe();
      this.cannedMessageOpenSubscription = null;
    }
    if (this.cannedMessageCloseSubscription) {
      this.cannedMessageCloseSubscription.unsubscribe();
      this.cannedMessageCloseSubscription = null;
    }
    if (this.cannedMessageSelectionSubscription) {
      this.cannedMessageSelectionSubscription.unsubscribe();
      this.cannedMessageSelectionSubscription = null;
    }
    if (this.cannedMessageFolderDropdownSubscription) {
      this.cannedMessageFolderDropdownSubscription.unsubscribe();
      this.cannedMessageFolderDropdownSubscription = null;
    }
    if (this._trackSelectedItemSubscription) {
      this._trackSelectedItemSubscription.unsubscribe();
      this._trackSelectedItemSubscription = null;
    }
    if (this.emailWorkItemUpdateSubscription) {
      this.emailWorkItemUpdateSubscription.unsubscribe();
      this.emailWorkItemUpdateSubscription = null;
    }
    if (this._laneObservable) {
      this._laneObservable.unsubscribe();
      this._laneObservable = null;
    }
    this.unregisterPanelCloseListeners();
    this._bcWorkItemsTrackingService.destroyItem(this.workItem.id);
  }

  private initTrackEvents(): void {
    this._trackSelectedItemSubscription = this._bcWorkItemsTrackingService.onChange.pipe(filter(id => id === this.workItem.id)).subscribe(() => {
      this._waitForTrack = true;
      if (this._dataIsLoaded) {
        this.trackSelectedEvent();
      }
    });
  }

  initMessageBus(): void {
    this.cannedMessageOpenSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.ActivateCannedMessagePanel}${this.workItem.id}`, () => {
      this._showAndOpenCannedMessagePanel();
    });

    this.cannedMessageCloseSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.CloseCannedMessagePanel}${this.workItem.id}`, () => {
      this.closeCannedMessagePanel();
    });

    this.cannedMessageSelectionSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.SendCannedMessageToEditor}${this.workItem.id}`, (args) => {
      if (args.eventSource === CannedMessagePanelComponent.name) {
        this.closeCannedMessagePanel();
      }
    });

    this.emailWorkItemUpdateSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.EmailWorkItemUpdate}${this.workItem.id}`, () => {
      const emailThreadId = this.getActualEmailThreadId();
      this.loadLastEmailFromThread(emailThreadId);
    });
  }

  initLaneObservable(): void {
    this._laneObservable = this._laneManager.getLaneObservable(this.workItem.id)
      .subscribe(lanes => {
        this.workItemLanes = lanes;
      });
  }

  get isCannedMessagesLaneVisible(): boolean {
    return this._isLaneVisible(WorkItemLaneType.CannedMessage);
  }

  get isVisitorInfoLaneVisible(): boolean {
    return this._isLaneVisible(WorkItemLaneType.VisitorInfo);
  }

  get isDiscussionsLaneVisible(): boolean {
    return this._isLaneVisible(WorkItemLaneType.Discussions);
  }

  private _showAndOpenCannedMessagePanel(): void {
    if (!this.isCannedMessagesLaneVisible) {
      this._laneManager.toggleLane(this.workItem.id, WorkItemLaneType.CannedMessage.toString(), this.workItem.type);
    }
    this.openCannedMessagePanel();
  }

  private _isLaneVisible(laneType: WorkItemLaneType): boolean {
    // Lane visibility togglers used only in expanded mode
    if (!this.isExpandedMode()) {
      return true;
    }

    const lane = this.workItemLanes && this.workItemLanes.find(workItemLane => workItemLane.type === laneType);
    return lane && lane.visible;
  }

  initEmail(): void {
    this.resetState();
    this.load();
  }

  resetState(): void {
    this.emailThread = null;
    this.lastEmail = null;

    this.themeClass = 'theme-1';
    this.visitor = null;
    this.isCannedMessagePanelOpen = false;
  }

  getActualEmailThreadId(): string {
    return this.workItem.id;
  }

  load(): void {
    const emailThreadId = this.getActualEmailThreadId();
    if (emailThreadId) {
      Promise.all([
        this.loadThreadInfo(emailThreadId),
        this.loadLastEmailFromThread(emailThreadId)
      ]).then(() => {
        this._dataIsLoaded = true;
        if (this._waitForTrack) {
          this.trackSelectedEvent();
        }
        this._changeDetector.detectChanges();
      });
    }
  }

  loadThreadInfo(emailThreadId): Promise<void> {
    return this._xlcEmails.getEmailThread(emailThreadId)
      .then(response => this.setEmailThread(response))
      .then(() => {
        this.buildVisitorInfo();
        this.setThemeClass();
      });
  }

  setEmailThread(emailThread): void {
    this.emailThread = emailThread;
  }

  loadLastEmailFromThread(emailThreadId: string): Promise<any> {
      this.lastEmail = null;

    return this._xlcEmails.getLastEmailFromThreadWithAttachments(emailThreadId)
      .then(email => {
        this.setEmail(email);
        return email;
      });
  }

  setEmail(email): void {
    this.lastEmail = email;

    if (this.lastEmail.attachments) {
      this.lastEmail.attachments.forEach(attachment => {
        if (attachment.downloadUrl && attachment.downloadUrl.substr(attachment.downloadUrl.length - 6) === '.bmail') {
          this.renameEmailAttachmentFileExtensionBmailToEml(attachment);
        }

        return attachment;
      });
    }

    this.cannedMessage = {
      type: bcEnums.cannedMessageTypes.EMAIL,
      emailId: this.lastEmail.id
    };
  }

  buildVisitorInfo(): Promise<void> {
    return this._bcVisitorInfoBuilderService.createVisitorDataForEmail(this.emailThread).then(
      (visitor) => {
        this.visitor = visitor;
        this.setDepartmentName();
      }
    );
  }

  setThemeClass(): void {
    if (this.emailThread) {
      this.themeClass = ThemeServiceHelper.getCssClass(this.emailThread.id);
    }
  }

  setDepartmentName(): Promise<void> {
    if (this.emailThread.departmentName) {
      this.visitor.fields.departmentName.value = this.emailThread.departmentName;
      return;
    }
    return this._bcVisitorInfoBuilderService.getDepartmentName(this.emailThread)
      .then(departmentName => {
        this.visitor.fields.departmentName.value = departmentName;
      });
  }

  isEmailThreadActive(): boolean {
    return this.emailThread && !this.emailThread.closed;
  }

  _sendCannedMessagePanelAnalytics(action: string): void {
    this._bcAnalytics.eventTrack('EmailCMPanel', { action });
  }

  private trackSelectedEvent(): void {
    this._waitForTrack = false;
    this._bcWorkItemsTrackingService.track(this.workItem);
  }

  private renameEmailAttachmentFileExtensionBmailToEml(emailAttachment: any): any {
    emailAttachment.fileName = emailAttachment.fileName.replace('.bmail', '.eml');
    emailAttachment.filename = emailAttachment.filename.replace('.bmail', '.eml');
    emailAttachment.shortFileName = emailAttachment.shortFileName.replace('.bmail', '.eml');
    emailAttachment.downloadUrl = emailAttachment.downloadUrl.replace('.bmail', '.eml');
    return emailAttachment;
  }
}
